import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL from '../api';

// Theme Constants (Identical to Super Admin)
const THEME = {
    primary: '#C0722C', // Gold/Copper
    secondary: '#2C1A10', // Dark Brown
    background: '#F8F6F2', // Cream
    sidebar: '#FFFFFF',
    text: '#333333'
};

const ICONS = {
    dashboard: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>,
    question: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>,
    agenda: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>,
    committee: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
    finance: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>,
    docs: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>,
    report: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>,
    logout: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>,
    timer: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
};

const SecretariatLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Session Timer Logic with Absolute Persistence
    const [timeLeft, setTimeLeft] = useState(() => {
        const expiry = localStorage.getItem('sessionExpiry');
        if (expiry) {
            const remaining = Math.floor((parseInt(expiry, 10) - Date.now()) / 1000);
            return remaining > 0 ? remaining : 0;
        }
        // Initialize if not present
        const newExpiry = Date.now() + 1800 * 1000; // Changed from 900 to 1800
        localStorage.setItem('sessionExpiry', newExpiry.toString());
        return 1800; // Changed from 900 to 1800
    });

    const [isCollapsed, setIsCollapsed] = useState(false);

    // Safety check for user data to prevent blank screen crashes
    const user = React.useMemo(() => {
        try {
            const saved = localStorage.getItem('user');
            if (saved && saved !== 'undefined') {
                return JSON.parse(saved);
            }
        } catch (e) {
            console.error('User parse error', e);
        }
        return { name: 'Secretary', role: 'Staff' };
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            const expiry = localStorage.getItem('sessionExpiry');
            if (expiry) {
                const remaining = Math.floor((parseInt(expiry, 10) - Date.now()) / 1000);
                if (remaining <= 0) {
                    setTimeLeft(0);
                    clearInterval(timer);
                    handleLogout('Session Expired');
                } else {
                    setTimeLeft(remaining);
                }
            }
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleLogout = async (reason = 'Logged out') => {
        try {
            await axios.post(`${API_BASE_URL}/api/logout`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
        } catch (e) { console.error('Logout failed', e); }
        localStorage.clear();
        navigate('/');
        Swal.fire({
            title: 'Secure Logout',
            text: reason,
            icon: 'info',
            timer: 2000,
            showConfirmButton: false,
            confirmButtonColor: THEME.primary
        });
    };

    const navItems = [
        { label: 'Dashboard', path: '/secretariat/dashboard', icon: ICONS.dashboard },
        { label: 'Question Bank', path: '/secretariat/questions', icon: ICONS.question },
        { label: 'Agenda Management', path: '/secretariat/agendas', icon: ICONS.agenda },
        { label: 'Committee Meetings', path: '/secretariat/meetings', icon: ICONS.committee },
        { label: 'Finance & Dues', path: '/secretariat/finance', icon: ICONS.finance },
        { label: 'Reports Center', path: '/secretariat/reports', icon: ICONS.report },
        { label: 'My Profile', path: '/secretariat/profile', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg> },
    ];

    return (
        <div style={{ display: 'flex', height: '100vh', backgroundColor: THEME.background, fontFamily: '"Outfit", sans-serif' }}>

            {/* Sidebar */}
            <div style={{
                width: isCollapsed ? '90px' : '280px',
                backgroundColor: THEME.sidebar,
                borderRight: '1px solid #EAE2D5',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '4px 0 20px rgba(0,0,0,0.03)',
                zIndex: 10,
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative'
            }}>
                <div style={{ padding: isCollapsed ? '40px 15px' : '40px 30px', borderBottom: '1px solid #F0E8DB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                        <div style={{
                            minWidth: '40px',
                            height: '40px',
                            backgroundColor: THEME.primary,
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                        }}>
                            {/* Using Docs icon as generic logo for Secretariat */}
                            {ICONS.docs}
                        </div>
                        {!isCollapsed && (
                            <div>
                                <h2 style={{ color: THEME.secondary, margin: 0, fontSize: '18px', fontWeight: '800' }}>VIDHAN SABHA</h2>
                                <span style={{ fontSize: '11px', color: THEME.primary, fontWeight: '700', textTransform: 'uppercase' }}>Secretariat Portal</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar Toggle */}
                <div
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    style={{
                        position: 'absolute',
                        right: '-12px',
                        top: '48px',
                        width: '24px',
                        height: '24px',
                        backgroundColor: THEME.sidebar,
                        border: '1px solid #EAE2D5',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 11,
                        color: THEME.primary
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none' }}>
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                </div>

                <nav style={{ flex: 1, padding: '30px 20px' }}>
                    {navItems.map((item) => (
                        <div
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            title={isCollapsed ? item.label : ''}
                            style={{
                                padding: '14px 20px',
                                margin: '8px 0',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                gap: '15px',
                                backgroundColor: location.pathname === item.path ? '#FDF8F3' : 'transparent',
                                color: location.pathname === item.path ? THEME.primary : '#6B6B6B',
                                fontWeight: location.pathname === item.path ? '700' : '500',
                                border: location.pathname === item.path ? `1px solid ${THEME.primary}20` : '1px solid transparent',
                                transition: 'all 0.3s'
                            }}
                        >
                            <span style={{ color: location.pathname === item.path ? THEME.primary : '#999' }}>{item.icon}</span>
                            {!isCollapsed && <span style={{ fontSize: '15px' }}>{item.label}</span>}
                        </div>
                    ))}
                </nav>

                <div style={{ padding: '20px', borderTop: '1px solid #F0E8DB', textAlign: 'center' }}>
                    <button
                        onClick={() => handleLogout()}
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: '#FFF5F5',
                            color: '#E53935',
                            border: '1.5px solid #FFEBEE',
                            borderRadius: '12px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}
                    >
                        {ICONS.logout}
                        {!isCollapsed && <span>Secure Exit</span>}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Header */}
                <div style={{
                    height: '80px',
                    backgroundColor: '#FFFFFF',
                    borderBottom: '1px solid #F0E8DB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 50px'
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: THEME.secondary }}>Secretariat Operations</h3>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>Bihar Vidhan Sabha • Authorized Staff Access</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
                        <div style={{
                            padding: '10px 20px',
                            backgroundColor: '#2C1A10',
                            color: '#EAD7BB',
                            borderRadius: '30px',
                            fontSize: '13px',
                            fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <span style={{ display: 'flex', color: THEME.primary }}>{ICONS.timer}</span>
                            <span>SESSION: {formatTime(timeLeft)}</span>
                        </div>
                        <div
                            onClick={() => navigate('/secretariat/profile')}
                            style={{ display: 'flex', alignItems: 'center', gap: '12px', borderLeft: '1px solid #EEE', paddingLeft: '25px', cursor: 'pointer' }}
                        >
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: THEME.secondary }}>{user.name || 'Secretary'}</div>
                                <div style={{ fontSize: '11px', color: THEME.primary, fontWeight: '600' }}>{user.role}</div>
                            </div>
                            <div style={{
                                width: '45px',
                                height: '45px',
                                borderRadius: '14px',
                                backgroundColor: THEME.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: '800',
                                fontSize: '18px'
                            }}>
                                {user.name ? user.name[0] : 'S'}
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '40px' }}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default SecretariatLayout;
